import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import BottomNav from './components/BottomNav';
import DesktopNav from './components/DesktopNav';
import SplashScreen from './components/SplashScreen';

// Pages
import HomePage from './pages/HomePage';
import MissingPersonsPage from './pages/MissingPersonsPage';
import UnknownPersonsPage from './pages/UnknownPersonsPage';
import SubmitReportPage from './pages/SubmitReportPage';
import CaseDetailPage from './pages/CaseDetailPage';
import SearchPage from './pages/SearchPage';
import FaceMatchPage from './pages/FaceMatchPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import SafetyPage from './pages/SafetyPage';
import RequestDeletePage from './pages/RequestDeletePage';
import ShokhSandeshPage from './pages/ShokhSandeshPage';
import ShokhSandeshSubmitPage from './pages/ShokhSandeshSubmitPage';
import ShokhSandeshDetailPage from './pages/ShokhSandeshDetailPage';
import AdminShokhCreatePage from './pages/AdminShokhCreatePage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';

import './index.css';

// Responsive Layout - Mobile has bottom nav, Desktop has top nav
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isAdminRoute) {
    return children;
  }
  
  return (
    <div className="app-container">
      {/* Desktop Navigation */}
      <DesktopNav />
      
      {/* Page Content */}
      <main className="md:page-content">
        {children}
      </main>
      
      {/* Mobile Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

// Router Component
const AppRouter = () => {
  return (
    <Routes>
      {/* Public Routes with Responsive Layout */}
      <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
      <Route path="/missing" element={<AppLayout><MissingPersonsPage /></AppLayout>} />
      <Route path="/unknown" element={<AppLayout><UnknownPersonsPage /></AppLayout>} />
      <Route path="/submit" element={<AppLayout><SubmitReportPage /></AppLayout>} />
      <Route path="/case/:caseId" element={<AppLayout><CaseDetailPage /></AppLayout>} />
      <Route path="/search" element={<AppLayout><SearchPage /></AppLayout>} />
      <Route path="/face-match" element={<AppLayout><FaceMatchPage /></AppLayout>} />
      <Route path="/privacy-policy" element={<AppLayout><PrivacyPolicyPage /></AppLayout>} />
      <Route path="/terms" element={<AppLayout><TermsConditionsPage /></AppLayout>} />
      <Route path="/safety" element={<AppLayout><SafetyPage /></AppLayout>} />
      <Route path="/request-delete" element={<AppLayout><RequestDeletePage /></AppLayout>} />
      <Route path="/shokh-sandesh" element={<AppLayout><ShokhSandeshPage /></AppLayout>} />
      <Route path="/shokh-sandesh/submit" element={<AppLayout><ShokhSandeshSubmitPage /></AppLayout>} />
      <Route path="/shokh-sandesh/:sandeshId" element={<AppLayout><ShokhSandeshDetailPage /></AppLayout>} />
      <Route path="/admin/shokh-create" element={<AppLayout><AdminShokhCreatePage /></AppLayout>} />
      
      {/* Admin Routes - Full Width */}
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
    </Routes>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const removeBranding = () => {
      document.querySelectorAll('a[href*="emergentagent"], a[href*="emergent.sh"], div[class*="emergent"], [id*="emergent"], [class*="emergent-badge"], [data-emergent]').forEach(el => el.remove());
      document.querySelectorAll('body > div, body > a, body > span, body > iframe').forEach(el => {
        if (el.id !== 'root' && !el.id?.startsWith('webpack') && (el.textContent?.includes('Emergent') || el.textContent?.includes('Made with') || el.innerHTML?.includes('emergent'))) el.remove();
      });
      document.querySelectorAll('script[src*="emergent"]').forEach(el => el.remove());
    };
    removeBranding();
    const interval = setInterval(removeBranding, 500);
    const observer = new MutationObserver(removeBranding);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { clearInterval(interval); observer.disconnect(); };
  }, []);

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
